import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { UserGroup } from '@/pages/MeetingDashboard/MeetingDashboard';
import { getMeetingType } from '@/pages/MeetingDashboard/MeetingDashboard';


// ─── Constants ────────────────────────────────────────────────────────────────

const VALID_PURPOSES = [
  { key: 'DISCOVERY',          label: 'Discovery' },
  { key: 'ROI_DISCUSSION',     label: 'ROI Discussion' },
  { key: 'OBJECTION_HANDLING', label: 'Objection Handling' },
  { key: 'REVIEW',             label: 'Review' },
  { key: 'INTRODUCTION',       label: 'Introduction' },
];
const VALID_OBJECTIVES = [
  { key: 'FOLLOW_UP_CALL', label: 'Follow Up Call' },
  { key: 'PROPOSAL',       label: 'Proposal' },
  { key: 'DEMO',           label: 'Demo' },
];
const CONTACT_TYPES = [
  { key: 'COLD',                  label: 'Cold' },
  { key: 'WARM',                  label: 'Warm' },
  { key: 'INBOUND_LEAD',          label: 'Inbound Lead' },
  { key: 'RE_ENGAGEMENT',         label: 'Reengagement' },
  { key: 'EXISTING_RELATIONSHIP', label: 'Existing Relationship' },
  { key: 'CURRENT_CUSTOMER',      label: 'Current Customer' },
];

const CONTACT_TYPE_DESCRIPTIONS: Record<string, string> = {
  cold:                   'No prior relationship with this prospect',
  warm:                   'Some prior engagement or referral in place',
  'inbound lead':         'Prospect reached out through website form',
  'existing relationship':'Ongoing relationship with this account',
  'current customer':     'Active customer being engaged for expansion',
  're-engagement':        'Reconnecting after a period of inactivity',
};

const PITCH_TYPE_DESCRIPTIONS: Record<string, string> = {
  call:        'Live sales call with a prospect or customer',
  demo:        'Product walkthrough demonstrating key features',
  negotiation: 'Discussing terms, pricing or contract details',
  objection:   'Addressing prospect concerns or pushback',
};

const STAGE_DESCRIPTIONS: Record<string, string> = {
  INTRODUCTION: 'Initial contact to understand needs and challenges',
  DISCOVERY:    'Exploring requirements and qualifying fit',
  PROPOSAL:     'Presenting the solution, scope and pricing',
  NEGOTIATION:  'Aligning on terms and commercials before close',
  CLOSING:      'Finalising the agreement and next steps',
  FOLLOW_UP:    'Checking in after a prior conversation',
};

const cap = (s: number) => Math.min(Math.round(s), 100);

// ─── Top-level classification: Scenario / Digital Twin / Pitch Activity ──────

const isScenarioSession = (m: any): boolean =>
  m.isScenario === true || getMeetingType(m) === 'scenario';

const isDigitalTwinPitch = (m: any): boolean => {
  if (isScenarioSession(m)) return false;
  const title = String(m.pitchTitle || m.otherDetails?.meetingType || '').toLowerCase();
  return title.includes('digital twin') || title.includes('digital-twin');
};

// ─── Main 3-way tab system ────────────────────────────────────────────────────

const PITCH_MAIN_TABS = [
  { key: 'pitchActivity', label: 'Pitch Activity', color: '#38bdf8' },
  { key: 'scenario',      label: 'Scenario',       color: '#2dd4bf' },
  { key: 'digitalTwin',   label: 'Digital Twin',   color: '#a78bfa' },
] as const;
type TabKey = typeof PITCH_MAIN_TABS[number]['key'];

const getTabKeyForMeeting = (m: any): TabKey => {
  if (isScenarioSession(m)) return 'scenario';
  if (isDigitalTwinPitch(m)) return 'digitalTwin';
  return 'pitchActivity';
};

const typeAvgScore = (items: any[], tabKey: TabKey): number => {
  const filtered = items.filter((m) => getTabKeyForMeeting(m) === tabKey);
  if (!filtered.length) return 0;
  return cap(filtered.reduce((a: number, m: any) => a + (m.overallPercentage || m.score || 0), 0) / filtered.length);
};

// Sub-type detail shown *inside* each card (Cold/Warm/etc, DT session title, scenario title)
const formatWords = (raw: string) =>
  raw
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');


const getContactType = (m: any): string =>
  String(m.otherDetails?.meetingType || '').toUpperCase();

const formatContactTypeLabel = (type: string): string => {
  const found = CONTACT_TYPES.find((ct) => ct.key === type.toUpperCase());
  return found ? found.label : formatWords(type);
};

const formatContactTypeDescription = (type: string): string => {
  const key = String(type || '').trim().toLowerCase().replace(/_/g, ' ');
  return CONTACT_TYPE_DESCRIPTIONS[key] || '';
};

const getSubTypeDetail = (m: any, tabKey: TabKey): { label: string; desc: string } => {
  if (tabKey === 'scenario') {
    const difficulty = m.TargetCompanyProfile?.businessModel || '';
    const personaStyle = m.companyContact?.jobTitle || '';
    return {
      label: m.pitchTitle || 'Practice Scenario',
      desc: [difficulty, personaStyle].filter(Boolean).join(' · ') || 'AI roleplay practice session',
    };
  }
  if (tabKey === 'digitalTwin') {
    return {
      label: m.pitchTitle || 'Digital Twin Session',
      desc: 'AI-simulated pitch with real-time video/audio analytics',
    };
  }
  const stage = Array.isArray(m.otherDetails?.stageofSalesCycle)
    ? m.otherDetails.stageofSalesCycle
    : m.otherDetails?.stageofSalesCycle
      ? [String(m.otherDetails.stageofSalesCycle)]
      : [];

  if (stage.length) {
    return {
      label: stage.map((s: string) => formatWords(s)).join(' / '),
      desc: STAGE_DESCRIPTIONS[(String(stage[0]) || '').toUpperCase()] || 'Current point in the active sales cycle',
    };
  }

  const raw = String(m.pitchTitle || m.otherDetails?.meetingType || m.meetingType || '');
  return {
    label: formatWords(raw) || 'Pitch Activity',
    desc: PITCH_TYPE_DESCRIPTIONS[getMeetingType(m)] || 'Type of pitch interaction recorded',
  };
};

// ─── Small formatters ─────────────────────────────────────────────────────────

const fmtDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const fmtTime = (iso: string) =>
  iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '';

const fmtDuration = (totalSeconds: number) => {
  if (!totalSeconds || totalSeconds < 0) return '—';
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const getDuration = (m: any): string => {
  const toSeconds = (val: number) => (val > 10800 ? val / 1000 : val);
  const rawSecs = m.duration || m.durationSeconds || m.callDurationSeconds || m.audioDurationSeconds;
  if (rawSecs) return fmtDuration(toSeconds(rawSecs));
  if (Array.isArray(m.transcriptJson) && m.transcriptJson.length) {
    const last = m.transcriptJson[m.transcriptJson.length - 1];
    const end = last?.end ?? last?.endTime;
    if (typeof end === 'number') return fmtDuration(toSeconds(end));
  }
  return '—';
};

const getCompanyName = (m: any): string =>
  m.TargetCompanyProfile?.companyName || m.customerData?.companyName || m.companyName || m.company || '';

const formatPurposeLabel = (purpose: string): string => {
  const found = VALID_PURPOSES.find((vp) => vp.key === purpose.toUpperCase());
  if (found) return found.label;
  return formatWords(purpose);
};

function scoreLabel(score: number): { text: string; color: string; bg: string } {
  if (score >= 90) return { text: 'Excellent', color: '#4ADE80', bg: 'rgba(74,222,128,0.12)' };
  if (score >= 70) return { text: 'Good', color: '#A3E635', bg: 'rgba(163,230,53,0.12)' };
  if (score >= 50) return { text: 'Fair', color: '#FACC15', bg: 'rgba(250,204,21,0.12)' };
  return { text: 'Poor', color: '#F87171', bg: 'rgba(248,113,113,0.12)' };
}

const STATUS: Record<string, { color: string; bg: string }> = {
  Active:     { color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  Processing: { color: '#38bdf8', bg: 'rgba(56,189,248,0.12)' },
  Completed:  { color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  Stalled:    { color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
};

// ─── Score Ring ───────────────────────────────────────────────────────────────

const ScoreRing = ({ score, size = 80 }: { score: number; size?: number }) => {
  const s    = cap(score);
  const r    = (size / 2) - 7;
  const circ = 2 * Math.PI * r;
  const dash = (s / 100) * circ;
  const sl   = scoreLabel(s);
  const color = s >= 70 ? '#4ade80' : s >= 40 ? '#facc15' : '#f87171';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="6"/>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            transform={`rotate(-90 ${size/2} ${size/2})`}
            style={{ filter: `drop-shadow(0 0 6px ${color}55)` }}/>
        </svg>
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <span style={{ fontSize: size <= 64 ? 15 : 19, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{s}%</span>
        </div>
      </div>
      <span style={{ fontSize: 13, fontWeight: 700, color }}>{sl.text}</span>
    </div>
  );
};

// ─── Shared visual atoms ──────────────────────────────────────────────────────

const eyebrowStyle: React.CSSProperties = {
  fontSize: 11.5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)',
  letterSpacing: '0.14em', fontWeight: 700, margin: '0 0 8px', display: 'block',
};

const badge = (color: string, filled = false): React.CSSProperties => ({
  fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
  background: filled ? `${color}1f` : 'rgba(255,255,255,0.06)',
  color:      filled ? color        : 'rgba(255,255,255,0.75)',
  border:     `1px solid ${filled ? color + '40' : 'rgba(255,255,255,0.1)'}`,
  display:    'inline-block', lineHeight: 1.4,
});

const IconAvatar = ({ color, children }: { color: string; children: React.ReactNode }) => (
  <div style={{
    width: 40, height: 40, borderRadius: 11, flexShrink: 0,
    background: `linear-gradient(135deg, ${color}26, ${color}0c)`,
    border: `1px solid ${color}3d`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    {children}
  </div>
);

const Icon = {
  Person: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
    </svg>
  ),
  Calendar: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
    </svg>
  ),
  Clock: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <circle cx="12" cy="12" r="9"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3"/>
    </svg>
  ),
  Handshake: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 11l2.5 2.5L15 9m-9 2l-3 3 3 3 2-2m9-7l3 3-3 3-2-2M6 13l4 4M14 9l4 4"/>
    </svg>
  ),
  Target: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.2" fill={c}/>
    </svg>
  ),
  Refresh: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M4 9a8 8 0 0114-5M20 15a8 8 0 01-14 5"/>
    </svg>
  ),
  Info: (c: string) => (
    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <circle cx="12" cy="12" r="9"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 8h.01M11 12h1v4h1"/>
    </svg>
  ),
  Scenario: (c: string) => (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
    </svg>
  ),
  DigitalTwin: (c: string) => (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke={c} strokeWidth={1.8}>
      <rect x="3" y="6" width="13" height="12" rx="2"/>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 10l5-3v10l-5-3"/>
    </svg>
  ),
};

// ─── Single pitch entry card ─────────────────────────────────────────────────

const PitchEntryCard = ({
  meeting, tabColor, tabLabel, tabKey,
}: { meeting: any; tabColor: string; tabLabel: string; tabKey: TabKey }) => {

  const navigate = useNavigate();
  const location = useLocation();

  const isScenario    = tabKey === 'scenario';
  const isDigitalTwin = tabKey === 'digitalTwin';

  const score     = cap(meeting.overallPercentage || meeting.score || 0);
  const createdAt = meeting.createdAt || meeting.recordedAt || '';
  const status    = meeting.status || 'Active';
  const stCfg     = STATUS[status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()] ?? STATUS.Active;
  const duration  = getDuration(meeting);

  const company   = getCompanyName(meeting);
  const industry  = meeting.TargetCompanyProfile?.industry    || meeting.customerData?.industry    || meeting.industry || '';
  const bizModel  = !isScenario ? (meeting.TargetCompanyProfile?.businessModel || meeting.businessModel || '') : '';
  const contact   = meeting.customerContact?.fullName
    || `${meeting.companyContact?.firstName || ''} ${meeting.companyContact?.lastName || ''}`.trim() || '—';
  const email     = meeting.companyContact?.email || meeting.customerContact?.email || '';
  const jobTitle  = !isScenario ? (meeting.companyContact?.jobTitle || '') : '';
  const phone     = meeting.companyContact?.phoneNumber || meeting.customerContact?.phoneNumber || '';
  const persona   = meeting.companyContact?.buyerPersona || [];
  const dealMaturity = !isScenario ? String(
    meeting.otherDetails?.dealMaturity || meeting.dealMaturity || ''
  ) : '';
  const purposes  = meeting.otherDetails?.meetingPurpose || [];
  const stage     = meeting.otherDetails?.stageofSalesCycle || [];
  const objective = meeting.otherDetails?.meetingObjective || [];
  const meetingTypeRaw = !isScenario ? String(meeting.otherDetails?.meetingType || '') : '';
  // const solutionFocus  = !isScenario ? normalizeField(meeting.otherDetails?.solutionFocus) : '';
  const stageLabel = stage.length ? stage.map((s: string) => formatWords(s)).join(' / ') : '—';
  const stageDesc  = STAGE_DESCRIPTIONS[(stage[0] || '').toUpperCase()] || 'Current point in the active sales cycle';

  const subType = getSubTypeDetail(meeting, tabKey);
  const scenarioTitle = meeting.pitchTitle || `Scenario ${(meeting.id || meeting._id || '').slice(0, 8)}`;

  const showContact = contact && contact !== '—' && !isScenario;
  let mainTitle = '';
  let subtitle = '';

  if (isScenario) {
    mainTitle = scenarioTitle;
    subtitle = (meeting.customerContact?.fullName && meeting.customerContact.fullName !== '—')
      ? meeting.customerContact.fullName
      : '';
  } else {
    if (showContact) {
      mainTitle = contact;
      subtitle = company;
    } else {
      mainTitle = company || (isDigitalTwin ? meeting.pitchTitle : '') || '—';
      subtitle = '';
    }
  }

  const avatarIcon = isScenario
    ? Icon.Scenario(tabColor)
    : isDigitalTwin
      ? Icon.DigitalTwin(tabColor)
      : Icon.Person(tabColor);

  return (
    <div style={{
      background: 'linear-gradient(160deg, #0d0d16 0%, #0a0a12 100%)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderLeft: `3px solid ${tabColor}`,
      borderRadius: 18,
      padding: '24px 28px',
    }}>
      {/* ── Top row ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 28, flexWrap: 'wrap' }}>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flex: '1 1 260px', minWidth: 220 }}>
          <IconAvatar color={tabColor}>{avatarIcon}</IconAvatar>
          <div style={{ minWidth: 0 }}>
            <span style={eyebrowStyle}>{isScenario ? 'Scenario' : isDigitalTwin ? 'Digital Twin' : 'Buyer Persona'}</span>

            {subtitle && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>{subtitle}</span>
                {industry && <span style={badge(tabColor, false)}>{industry}</span>}
                {bizModel && <span style={badge(tabColor, false)}>{bizModel}</span>}
              </div>
            )}

            <p style={{ fontSize: 17, fontWeight: 700, color: '#fff', margin: '0 0 2px' }}>{mainTitle}</p>

            {!subtitle && (industry || bizModel) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                {industry && <span style={badge(tabColor, false)}>{industry}</span>}
                {bizModel && <span style={badge(tabColor, false)}>{bizModel}</span>}
              </div>
            )}

            {!isScenario && (
              <>
                {jobTitle && <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.9)', margin: '0 0 4px' }}>{jobTitle}</p>}
                {email && (
                  <p style={{ fontSize: 13, color: '#38bdf8', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 230 }}>
                    {email}{phone ? ` · ${phone}` : ''}
                  </p>
                )}
                {persona.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {persona.slice(0, 3).map((p: string) => (
                      <span key={p} style={badge(tabColor, true)}>{formatWords(p)}</span>
                    ))}
                  </div>
                )}
               
              </>
            )}
            
          </div>
        </div>
            <div style={{ flex: '0 0 150px' }}>
              {dealMaturity && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6,  }}>
                          <span style={eyebrowStyle}>Deal Maturity</span>
                          <span style={badge(tabColor, false)}>{formatWords(dealMaturity)}</span>
                        </div>
                      )}
            </div>

        <div style={{ flex: '0 0 150px' }}>
          <span style={eyebrowStyle}>Timestamp</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {Icon.Calendar(tabColor)}
            <div>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>{fmtDate(createdAt)}</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', margin: 0 }}>{fmtTime(createdAt)}</p>
            </div>
          </div>
        </div>

        <div style={{ flex: '0 0 110px' }}>
          <span style={eyebrowStyle}>Duration</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {Icon.Clock(tabColor)}
            <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>{duration}</p>
          </div>
        </div>

        <div style={{ flex: '0 0 100px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ ...eyebrowStyle, textAlign: 'center' }}>Pitchster Score</span>
          <ScoreRing score={score} size={72} />
        </div>
        <div style={{ flex: '0 0 auto', marginLeft: 'auto' }}>
          <button
            onClick={() => {
              const st = (location.state as any) || {};
              const repObj = st.selectedRep || st.rep || (meeting.userId ? { id: meeting.userId, userId: meeting.userId, name: meeting.User?.name || meeting.userName || meeting.salesRep } : null);
              navigate(`/meeting-detail/${meeting.id || meeting._id}`, { state: { ...st, meeting, from: location.pathname + location.search, selectedRep: repObj, repTab: 'pitches' } });
            }}
            style={{
              background: `linear-gradient(135deg, ${tabColor}18, ${tabColor}08)`,
              border: `1px solid ${tabColor}45`, color: tabColor,
              padding: '10px 18px', borderRadius: 11, fontSize: 13.5, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap',
            }}
          >
            View Details
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12"/>
            </svg>
          </button>
        </div>
      </div>

      <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', margin: '20px 0' }}/>

      {/* ── Bottom row ── */}
      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {/* Primary sub-type area: prefer Stage of Sales Cycle when available */}
        {!isScenario && stage.length > 0 ? (
          <div style={{ display: 'flex', gap: 12, flex: '1 1 220px', minWidth: 200 }}>
            <IconAvatar color={tabColor}>{Icon.Handshake(tabColor)}</IconAvatar>
            <div>
              <span style={eyebrowStyle}>Stage of Sales Cycle</span>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: '0 0 3px' }}>{stageLabel}</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', margin: 0, lineHeight: 1.4 }}>{stageDesc}</p>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, flex: '1 1 220px', minWidth: 200 }}>
            <IconAvatar color={tabColor}>{Icon.Target(tabColor)}</IconAvatar>
            <div>
              <span style={eyebrowStyle}>{tabLabel}</span>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: '0 0 3px' }}>{subType.label}</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', margin: 0, lineHeight: 1.4 }}>{subType.desc}</p>
            </div>
          </div>
        )}

        {!isScenario && meetingTypeRaw && (
          <div style={{ display: 'flex', gap: 12, flex: '1 1 220px', minWidth: 200 }}>
            <IconAvatar color={tabColor}>{Icon.Info(tabColor)}</IconAvatar>
            <div>
              <span style={eyebrowStyle}>Type of Contact</span>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: '0 0 3px' }}>{formatContactTypeLabel(meetingTypeRaw)}</p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', margin: 0, lineHeight: 1.4 }}>
                {formatContactTypeDescription(meetingTypeRaw) || formatWords(meetingTypeRaw)}
              </p>
            </div>
          </div>
        )}

        {!isScenario && (purposes.length > 0 || objective.length > 0) && (
          <div style={{ flex: '1 1 220px', minWidth: 200, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {purposes.length > 0 && (
              <div>
                <span style={eyebrowStyle}>Type of Interaction</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {purposes.map((p: string) => <span key={p} style={badge(tabColor, true)}>{formatPurposeLabel(p)}</span>)}
                </div>
              </div>
            )}
            {objective.length > 0 && (
              <div>
                <span style={eyebrowStyle}>Objective</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {objective.map((o: string) => {
                    const found = VALID_OBJECTIVES.find((vo) => vo.key === o.toUpperCase());
                    return <span key={o} style={badge(tabColor, false)}>{found ? found.label : formatWords(o)}</span>;
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ flex: '0 0 150px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <span style={eyebrowStyle}>Status</span>
            <span style={{
              fontSize: 13, fontWeight: 700, padding: '4px 13px', borderRadius: 999,
              background: stCfg.bg, color: stCfg.color, border: `1px solid ${stCfg.color}35`,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: stCfg.color }}/>
              {status}
            </span>
          </div>
          {/* <div>
            <span style={eyebrowStyle}>Updated</span>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0 }}>{fmtDate(updatedAt)}</p>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.9)', margin: 0 }}>{fmtTime(updatedAt)}</p>
          </div> */}
        </div>
      </div>
    </div>
  );
};

// ─── Main Card ────────────────────────────────────────────────────────────────

export const PitchCardMain: React.FC<{ group: UserGroup }> = ({ group }) => {
  const [selectedType, setSelectedType] = useState<TabKey>('pitchActivity');
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [selectedContactType, setSelectedContactType] = useState<string>('all');
  const [selectedMeetingType, setSelectedMeetingType] = useState<string>('all');

  const statusCounts: Record<string, number> = {};
  group.meetings.forEach((m) => {
    const s = m.status
      ? (m.status.charAt(0).toUpperCase() + m.status.slice(1).toLowerCase())
      : 'Active';
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  });
  const dominantStatus = Object.entries(statusCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Active';
  const stCfg = STATUS[dominantStatus] ?? STATUS.Active;

  const tabCount: Record<TabKey, number> = useMemo(() => {
    const counts: Record<TabKey, number> = { pitchActivity: 0, scenario: 0, digitalTwin: 0 };
    group.meetings.forEach((m) => {
      counts[getTabKeyForMeeting(m)]++;
    });
    return counts;
  }, [group.meetings]);

  const scenarioCount    = tabCount.scenario;
  const digitalTwinCount = tabCount.digitalTwin;

  // Company options — only from non-scenario, non-DT meetings that carry real company data
  const companyOptions = useMemo(() => {
    const set = new Set<string>();
    group.meetings.forEach((m) => {
      if (getTabKeyForMeeting(m) === 'pitchActivity') {
        const c = getCompanyName(m);
        if (c) set.add(c);
      }
    });
    return Array.from(set).sort();
  }, [group.meetings]);

  // Meeting type options (from otherDetails.meetingType) for pitchActivity meetings
  const selectType = (key: TabKey) => {
    setSelectedType(key);
  };

  const resetFilters = () => {
    setSelectedType('pitchActivity');
    setSelectedCompany('all');
    setSelectedContactType('all');
    setSelectedMeetingType('all');
  };

  const visibleMeetings = useMemo(() => {
    return group.meetings
      .filter((m) => {
        const key = getTabKeyForMeeting(m);
        if (key !== selectedType) return false;
        if (selectedCompany !== 'all') {
          if (key !== 'pitchActivity') return false;
          if (getCompanyName(m) !== selectedCompany) return false;
        }
        if (selectedContactType !== 'all') {
          if (key !== 'pitchActivity') return false;
          if (getContactType(m) !== selectedContactType) return false;
        }
        if (selectedMeetingType !== 'all') {
          if (key !== 'pitchActivity') return false;
          const mt = String(m.otherDetails?.meetingType || '');
          if (mt !== selectedMeetingType) return false;
        }
        return true;
      })
      .sort((a: any, b: any) => {
        const da = new Date(a.createdAt || a.recordedAt || 0).getTime();
        const db = new Date(b.createdAt || b.recordedAt || 0).getTime();
        return db - da;
      });
  }, [group.meetings, selectedType, selectedCompany, selectedContactType, selectedMeetingType]);

  const latestDate = group.latestMeetingDate
    ? new Date(group.latestMeetingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';


  return (
    <>
      <div style={{
        background: 'linear-gradient(160deg, #09090f 0%, #07070d 100%)',
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: 20,
        marginBottom: 16,
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        maxHeight: 680,
        overflowY: 'auto',
      }}>

        <div style={{ position: 'sticky', top: 0, zIndex: 5, background: '#0a0a11' }}>

          {/* ── HEADER ── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 20, padding: '22px 26px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}>
            <ScoreRing score={group.avgScore} size={76} />

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#ffffff' }}>{group.userName}</span>
                <span style={{
                  fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999,
                  background: stCfg.bg, color: stCfg.color, border: `1px solid ${stCfg.color}35`,
                  display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: stCfg.color }}/>
                  {dominantStatus}
                </span>
                {group.activeCount > 0 && (
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, background: 'rgba(52,211,153,0.1)', color: '#34d399', border: '1px solid rgba(52,211,153,0.28)' }}>
                    {group.activeCount} live
                  </span>
                )}
                {scenarioCount > 0 && (
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, background: 'rgba(45,212,191,0.1)', color: '#2dd4bf', border: '1px solid rgba(45,212,191,0.28)' }}>
                    {scenarioCount} scenario
                  </span>
                )}
                {digitalTwinCount > 0 && (
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, background: 'rgba(167,139,250,0.1)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.28)' }}>
                    {digitalTwinCount} digital twin
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 17, color: '#fff', fontWeight: 500 }}>
                  {group.totalMeetings} pitch{group.totalMeetings !== 1 ? 'es' : ''}
                </span>
                <span style={{ color: '#374151' }}>·</span>
                <span style={{ fontSize: 17, color: '#fff' }}>{latestDate}</span>
                <span style={{ color: '#374151' }}>·</span>
                <span style={{ fontSize: 15, color: 'rgba(255,255,255,0.9)' }}>
                  Talk {group.talkRatio.agent}% / {group.talkRatio.customer}%
                </span>
                {visibleMeetings.length !== group.meetings.length && (
                  <>
                    <span style={{ color: '#374151' }}>·</span>
                    <span style={{ fontSize: 15, color: 'rgba(255,255,255,0.9)' }}>{visibleMeetings.length} shown</span>
                  </>
                )}
              </div>
            </div>

            {/* <button
              onClick={openCoachingModal}
              style={{
                display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0,
                padding: '11px 20px', borderRadius: 12, fontSize: 13.5, fontWeight: 700,
                background: 'linear-gradient(135deg, rgba(184,248,144,0.12), rgba(184,248,144,0.05))',
                color: '#B8F890', border: '1px solid rgba(184,248,144,0.28)',
                cursor: 'pointer', boxShadow: '0 4px 20px rgba(184,248,144,0.1)', whiteSpace: 'nowrap',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#B8F890" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              AI Recommendation
            </button> */}
          </div>

          {/* ── FILTER BAR ── */}
          <div style={{ padding: '16px 26px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>

            {companyOptions.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="rgba(255,255,255,0.7)" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01"/>
                </svg>
                <select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  style={{
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff', fontSize: 13.5, fontWeight: 600, padding: '8px 12px',
                    borderRadius: 10, cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="all" style={{ background: '#0d0d16' }}>All Companies</option>
                  {companyOptions.map((c) => (
                    <option key={c} value={c} style={{ background: '#0d0d16' }}>{c}</option>
                  ))}
                </select>
              </div>
            )}
               {selectedType === 'pitchActivity' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* {meetingTypeOptions.length > 0 && (
                      <select
                        value={selectedMeetingType}
                        onChange={(e) => setSelectedMeetingType(e.target.value)}
                        style={{
                          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                          color: '#fff', fontSize: 13.5, fontWeight: 600, padding: '8px 12px',
                          borderRadius: 10, cursor: 'pointer', outline: 'none',
                        }}
                      >
                        <option value="all" style={{ background: '#0d0d16' }}>All Interaction Types</option>
                        {meetingTypeOptions.map((t) => (
                          <option key={t} value={t} style={{ background: '#0d0d16' }}>{formatWords(String(t))}</option>
                        ))}
                      </select>
                    )} */}

                    <select
                      value={selectedContactType}
                      onChange={(e) => setSelectedContactType(e.target.value)}
                      style={{
                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                        color: '#fff', fontSize: 13.5, fontWeight: 600, padding: '8px 12px',
                        borderRadius: 10, cursor: 'pointer', outline: 'none',
                      }}
                    >
                      <option value="all" style={{ background: '#0d0d16' }}>All Contact Types</option>
                      {CONTACT_TYPES.map((ct) => (
                        <option key={ct.key} value={ct.key} style={{ background: '#0d0d16' }}>{ct.label}</option>
                      ))}
                    </select>
              </div>
            )}

            <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.08)' }}/>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {PITCH_MAIN_TABS.map((tab) => {
                const active = selectedType === tab.key;
                const cnt    = tabCount[tab.key as TabKey] ?? 0;
                const ts     = typeAvgScore(group.meetings, tab.key as TabKey);
                return (
                  <button
                    key={tab.key}
                    onClick={() => selectType(tab.key as TabKey)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 7,
                      padding: '8px 14px', borderRadius: 999, fontSize: 13, fontWeight: 700,
                      cursor: 'pointer', transition: 'all 0.15s',
                      background: active ? `${tab.color}16` : 'rgba(255,255,255,0.03)',
                      color:      active ? tab.color : 'rgba(255,255,255,0.7)',
                      border:     `1px solid ${active ? tab.color + '45' : 'rgba(255,255,255,0.07)'}`,
                    }}
                  >
                    {tab.label}
                    <span style={{
                      fontSize: 11.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
                      background: active ? `${tab.color}28` : 'rgba(255,255,255,0.06)',
                      color:      active ? tab.color : 'rgba(255,255,255,0.7)',
                    }}>{cnt}</span>
                    {cnt > 0 && active && (
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: `${tab.color}cc` }}>{ts}%</span>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={resetFilters}
              style={{
                marginLeft: 'auto', background: 'transparent', border: 'none',
                color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                padding: '6px 4px', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {Icon.Refresh('rgba(255,255,255,0.7)') }
              Reset
            </button>
            
          </div>

        </div>

        {/* ── PITCH LIST ── */}
        <div style={{ padding: '20px 26px 26px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {visibleMeetings.length === 0 ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160,
              background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
              borderRadius: 16, color: 'rgba(255,255,255,0.9)', fontSize: 15, fontWeight: 600,
            }}>
              No sessions match the selected filters
            </div>
          ) : (
            visibleMeetings.map((m: any) => {
              const key   = getTabKeyForMeeting(m);
              const tab   = PITCH_MAIN_TABS.find((t) => t.key === key);
              const color = tab?.color || '#38bdf8';
              const label = tab?.label || 'Pitch Activity';
              return (
                <PitchEntryCard key={m.id || m._id} meeting={m} tabColor={color} tabLabel={label} tabKey={key} />
              );
            })
          )}
        </div>
      </div>

    </>
  );
};

export default PitchCardMain;